<template>
  <div class="video-detail">
    <div v-if="video" class="detail-container">
      <button class="back-button" @click="goBack">
        ← 返回列表
      </button>

      <div class="video-player-section">
        <div class="video-wrapper">
          <video
            ref="videoPlayer"
            :src="currentVideoUrl"
            :poster="video.thumbnail"
            controls
            class="video-player"
          >
            您的浏览器不支持视频播放
          </video>
        </div>
      </div>

      <div class="related-videos" v-if="relatedVideos.length > 0">
        <div class="related-list">
          <div
            v-for="(relatedVideo, index) in relatedVideos"
            :key="index"
            :class="['related-card', { 'is-active': currentVideoUrl === relatedVideo.videoUrl }]"
            @click="playRelatedVideo(relatedVideo.videoUrl)"
          >
            <div class="related-thumbnail">
              <img :src="relatedVideo.thumbnail" :alt="relatedVideo.title" />
              <div class="play-icon">▶</div>
            </div>
            <div class="related-info">
              <div class="related-meta">
                <span class="related-title-text">{{ relatedVideo.title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="video-content">
        <div class="video-info">
          <div class="video-meta">
            <div class="meta-left">
              <h2 class="video-student">{{ video.studentName }}</h2>
            </div>
            <div class="meta-right">
              <div class="campus-badge">
                <span class="campus-icon">📍</span>
                <span class="campus-value">{{ video.campus }}</span>
              </div>
            </div>
          </div>
          <div class="video-scores">
            <div class="score-item">
              <span class="score-label">音乐教育成绩</span>
              <span class="score-value">{{ formatScore(video.musicEducationScore || 0) }}分</span>
            </div>
            <div class="score-item">
              <span class="score-label">音乐表演器乐成绩</span>
              <span class="score-value">{{ formatScore(video.instrumentPerformanceScore || 0) }}分</span>
            </div>
            <div class="score-item">
              <span class="score-label">音乐表演声乐成绩</span>
              <span class="score-value">{{ formatScore(video.vocalPerformanceScore || 0) }}分</span>
            </div>
          </div>
          <div class="video-description-section">
            <h3 class="description-title">简介</h3>
            <p class="video-description">{{ video.fullDescription || video.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="loading">
      <p>加载中...</p>
    </div>
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button class="back-button" @click="goBack">返回列表</button>
    </div>
  </div>
</template>

<script>
import { getStudentDetail } from '../api/scoreApi'

export default {
  name: 'VideoDetail',
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      video: null,
      relatedVideos: [],
      currentVideoUrl: '',
      loading: true,
      error: null
    }
  },
  methods: {
    async loadStudentDetail() {
      this.loading = true
      this.error = null

      try {
        const result = await getStudentDetail(parseInt(this.id))

        if (result.success && result.data) {
          this.video = result.data
          // 加载相关视频（同步方法，不需要await）
          this.loadRelatedVideos()
          // 设置默认视频为第一个可用视频
          this.setDefaultVideo()
        } else {
          this.error = result.message || '未找到该学生信息'
          // 如果学生不存在，3秒后跳转到列表页
          setTimeout(() => {
            this.$router.push('/')
          }, 3000)
        }
      } catch (error) {
        console.error('加载失败:', error)
        this.error = '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    loadRelatedVideos() {
      if (!this.video) {
        this.relatedVideos = []
        return
      }

      const videos = []

      // 器乐视频1
      if (this.video.instrumentalVideo1) {
        videos.push({
          videoUrl: this.video.instrumentalVideo1,
          title: this.video.instrumentalVideo1Desc || '器乐视频1',
          thumbnail: this.generateThumbnail(this.video.instrumentalVideo1)
        })
      }

      // 器乐视频2
      if (this.video.instrumentalVideo2) {
        videos.push({
          videoUrl: this.video.instrumentalVideo2,
          title: this.video.instrumentalVideo2Desc || '器乐视频2',
          thumbnail: this.generateThumbnail(this.video.instrumentalVideo2)
        })
      }

      // 声乐视频1
      if (this.video.vocalVideo1) {
        videos.push({
          videoUrl: this.video.vocalVideo1,
          title: this.video.vocalVideo1Desc || '声乐视频1',
          thumbnail: this.generateThumbnail(this.video.vocalVideo1)
        })
      }

      // 声乐视频2
      if (this.video.vocalVideo2) {
        videos.push({
          videoUrl: this.video.vocalVideo2,
          title: this.video.vocalVideo2Desc || '声乐视频2',
          thumbnail: this.generateThumbnail(this.video.vocalVideo2)
        })
      }

      this.relatedVideos = videos
    },
    setDefaultVideo() {
      if (!this.video) {
        this.currentVideoUrl = ''
        return
      }

      // 按优先级选择第一个可用视频：器乐视频1 > 器乐视频2 > 声乐视频1 > 声乐视频2
      if (this.video.instrumentalVideo1) {
        this.currentVideoUrl = this.video.instrumentalVideo1
      } else if (this.video.instrumentalVideo2) {
        this.currentVideoUrl = this.video.instrumentalVideo2
      } else if (this.video.vocalVideo1) {
        this.currentVideoUrl = this.video.vocalVideo1
      } else if (this.video.vocalVideo2) {
        this.currentVideoUrl = this.video.vocalVideo2
      } else {
        // 如果没有视频，使用原来的 videoUrl 作为后备
        this.currentVideoUrl = this.video.videoUrl || ''
      }

      // 等待 DOM 更新后加载视频
      this.$nextTick(() => {
        if (this.$refs.videoPlayer && this.currentVideoUrl) {
          this.$refs.videoPlayer.load()
        }
      })
    },
    generateThumbnail(videoUrl) {
      if (!videoUrl) {
        return `https://via.placeholder.com/640x360/ed6d00/ffffff?text=视频`
      }
      // 从视频URL生成缩略图URL
      // 格式：cjdb.xyyxpx.com/{campus}/{video_path}.mp4 -> cjdb.xyyxpx.com/{campus}/{video_path}.jpg
      const thumbnailUrl = videoUrl.replace('.mp4', '.jpg')
      return thumbnailUrl
    },
    playRelatedVideo(videoUrl) {
      if (!videoUrl) return

      // 更新当前视频URL
      this.currentVideoUrl = videoUrl

      // 等待 DOM 更新后切换视频
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.$refs.videoPlayer.load()
          this.$refs.videoPlayer.play().catch(err => {
            console.log('自动播放失败，用户需要手动播放:', err)
          })
          // 滚动到视频播放器位置
          this.$refs.videoPlayer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    goBack() {
      this.$router.push('/')
    },
    goToDetail(id) {
      this.$router.push(`/video/${id}`)
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
      // 重新加载数据
      this.loadStudentDetail()
    },
    formatScore(score) {
      return parseFloat(score).toFixed(2)
    }
  },
  mounted() {
    this.loadStudentDetail()
  },
  watch: {
    id() {
      // 当路由参数变化时，重新加载数据
      this.loadStudentDetail()
    }
  }
}
</script>

<style scoped>
.video-detail {
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
  background: #f5f5f5;
}

.detail-container {
  max-width: 1200px;
  margin: 0 auto;
}

.back-button {
  background: #fff;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 1.25rem;
  transition: all 0.3s;
}

.back-button:hover {
  border-color: #ed6d00;
  color: #ed6d00;
}

.video-player-section {
  margin-bottom: 1.5rem;
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 比例 */
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.video-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-content {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.video-info {
  padding: 0;
}

.video-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 2px solid #f0f0f0;
  flex-wrap: nowrap;
}

.meta-left {
  flex: 1;
  min-width: 0;
}

.video-student {
  color: #000;
  font-weight: 600;
  font-size: 1.5rem;
  letter-spacing: 0.3px;
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-right {
  margin-left: 1.25rem;
  flex-shrink: 0;
}

.campus-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #fff4ed 0%, #ffe8d6 100%);
  border: 1px solid #ffd4a3;
  border-radius: 20px;
  font-size: 0.875rem;
  white-space: nowrap;
}

.campus-icon {
  font-size: 1rem;
}

.campus-value {
  color: #ed6d00;
  font-weight: 600;
}

.video-scores {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.875rem;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.75rem;
  background: #fff;
  border-radius: 6px;
  border-left: 3px solid #ed6d00;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.score-item:hover {
  box-shadow: 0 4px 12px rgba(237, 109, 0, 0.15);
  transform: translateX(4px);
  border-left-width: 4px;
}

.score-label {
  color: #333;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.score-value {
  color: #ed6d00;
  font-weight: 700;
  font-size: 0.9375rem;
  min-width: 80px;
  text-align: right;
  letter-spacing: 0.3px;
}

.video-description-section {
  margin-top: 0;
}

.description-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #333;
}

.video-description {
  font-size: 0.9375rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

.related-videos {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.related-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.875rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.3s;
  padding: 0.5rem;
  border-radius: 6px;
  background: #f9f9f9;
  border: 2px solid transparent;
}

.related-card:hover {
  background-color: #fff4ed;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(237, 109, 0, 0.15);
}

.related-card.is-active {
  background-color: #fff4ed;
  border-color: #ed6d00;
}

.related-thumbnail {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 比例 */
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.related-thumbnail img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-info {
  flex: 1;
  display: flex;
  align-items: flex-start;
}

.related-meta {
  font-size: 0.8125rem;
  width: 100%;
}

.related-title-text {
  color: #000;
  font-weight: 500;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8125rem;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  background: rgba(237, 109, 0, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 1;
}

.related-card:hover .play-icon {
  opacity: 1;
}

.loading {
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
  font-size: 1.125rem;
}

.error {
  text-align: center;
  padding: 4rem 2rem;
  color: #f56c6c;
  font-size: 1.125rem;
}

.error .back-button {
  margin-top: 1.5rem;
}

@media (max-width: 768px) {
  .video-detail {
    padding: 0.5rem;
  }

  .video-content,
  .related-videos {
    padding: 1rem;
  }

  .video-meta {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .video-student {
    font-size: 1.25rem;
    white-space: normal;
  }

  .meta-right {
    margin-left: 0;
  }

  .campus-badge {
    font-size: 0.8125rem;
    padding: 0.4375rem 0.875rem;
  }

  .video-scores {
    padding: 0.75rem;
    gap: 0.5rem;
    margin-bottom: 0.875rem;
  }

  .score-item {
    padding: 0.5rem 0.625rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
  }

  .score-label {
    font-size: 0.8125rem;
  }

  .score-value {
    font-size: 0.875rem;
    min-width: auto;
    text-align: left;
  }

  .video-campus {
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
  }

  .related-list {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
  }

  .related-card {
    padding: 0.5rem;
  }

  .related-thumbnail {
    margin-bottom: 0.5rem;
  }

  .related-meta {
    font-size: 0.875rem;
  }

  .related-title-text {
    -webkit-line-clamp: 2;
  }
}
</style>

